import { resolveParticipantUserId } from "./resolve-participant-user-id.js";
import type { MeetingClient, TableCallStatus } from "./table-call.context.js";

export type RemoteCallPhase = "joining" | "ready";
export type LocalCallPhase =
  | "idle"
  | "joining"
  | "joined"
  | "reconnecting"
  | "terminal";

export interface LocalCallSnapshot {
  generation: number;
  phase: LocalCallPhase;
  revision: number;
  sequence: number;
}

export interface RemoteCallState {
  peerId: string;
  participantId: string;
  phase: RemoteCallPhase;
  audioEnabled: boolean;
  consumerTrackId?: string | undefined;
  participantRevision: number;
}

export interface RemoteAudioProjection {
  remote: RemoteCallState;
  track: MediaStreamTrack | null;
}

export interface ParticipantSnapshot {
  remote: RemoteCallState | null;
  sequence: number;
}

type Participant = NonNullable<
  ReturnType<MeetingClient["participants"]["joined"]["get"]>
>;
type SnapshotListener = () => void;

interface MeetingLifecycleHandlers {
  getRoomLeftPhase?: (state: string) => LocalCallPhase;
  retainRemotesOnRoomLeft?: (state: string) => boolean;
  isCurrent?: () => boolean;
  onRoomJoined?: () => void;
  onRoomLeft?: (state: string) => void;
}

const EMPTY_PARTICIPANT_SNAPSHOT: ParticipantSnapshot = {
  remote: null,
  sequence: 0,
};
const INITIAL_LOCAL_CALL_SNAPSHOT: LocalCallSnapshot = {
  generation: 0,
  phase: "idle",
  revision: 0,
  sequence: 0,
};

const PARTICIPANT_EVENTS = [
  "participantJoined",
  "participantLeft",
  "participantsCleared",
  "participantsUpdate",
] as const;
const RECOVERING_MEDIA_CONNECTION_STATES = new Set([
  "disconnected",
  "failed",
  "reconnecting",
]);

export class TableCallParticipantDirectory {
  private meeting: MeetingClient | null = null;
  private generation = 0;
  private isOperationCurrent: (() => boolean) | null = null;
  private projectionSequence = 0;
  private participantRevisionByPeerId = new Map<string, number>();
  private remotesByPeerId = new Map<string, RemoteCallState>();
  private consumerTracksByPeerId = new Map<string, MediaStreamTrack>();
  private screenShareAudioTracksByPeerId = new Map<string, MediaStreamTrack>();
  private joinOrderByPeerId = new Map<string, number>();
  private nextJoinOrder = 0;
  private snapshotsByUserId = new Map<string, ParticipantSnapshot>();
  private listenersByUserId = new Map<string, Set<SnapshotListener>>();
  private listeners = new Set<SnapshotListener>();
  private localCallSnapshot = INITIAL_LOCAL_CALL_SNAPSHOT;
  private localCallListeners = new Set<SnapshotListener>();
  private audioUpdateListeners = new Map<Participant, () => void>();
  private meetingListenerCleanups = new Set<() => void>();

  getSnapshot(userId: string): ParticipantSnapshot {
    return this.snapshotsByUserId.get(userId) ?? EMPTY_PARTICIPANT_SNAPSHOT;
  }

  getRemotes(): readonly RemoteCallState[] {
    return [...this.remotesByPeerId.values()];
  }

  getParticipantRemotes(): readonly RemoteCallState[] {
    return [...this.snapshotsByUserId.values()].flatMap((snapshot) =>
      snapshot.remote ? [snapshot.remote] : []
    );
  }

  getRemoteAudio(): readonly RemoteAudioProjection[] {
    return [...this.remotesByPeerId.values()].map((remote) => ({
      remote,
      track: this.consumerTracksByPeerId.get(remote.peerId) ?? null,
    }));
  }

  getReadyAudio(): readonly RemoteAudioProjection[] {
    return this.getRemoteAudio().filter(
      ({ remote }) => remote.phase === "ready"
    );
  }

  getScreenShareAudio(): readonly RemoteAudioProjection[] {
    return [...this.remotesByPeerId.values()].flatMap((remote) => {
      const track = this.screenShareAudioTracksByPeerId.get(remote.peerId);
      return track ? [{ remote, track }] : [];
    });
  }

  getGeneration(): number {
    return this.generation;
  }

  getRevision(): number {
    return this.projectionSequence;
  }

  getLocalCallSnapshot(): LocalCallSnapshot {
    return this.localCallSnapshot;
  }

  subscribeLocalCall(listener: SnapshotListener): () => void {
    this.localCallListeners.add(listener);
    return () => this.localCallListeners.delete(listener);
  }

  setLocalCallPhase(phase: LocalCallPhase, generation: number): void {
    if (generation < this.localCallSnapshot.generation) {
      return;
    }
    if (
      this.localCallSnapshot.generation === generation &&
      this.localCallSnapshot.phase === phase
    ) {
      return;
    }
    const sequence = this.nextProjectionSequence();
    this.localCallSnapshot = {
      generation,
      phase,
      revision: sequence,
      sequence,
    };
    for (const listener of this.localCallListeners) {
      listener();
    }
    this.updateParticipantProjectionSequence(sequence);
  }

  subscribe(userId: string, listener: SnapshotListener): () => void {
    const userListeners = this.listenersByUserId.get(userId) ?? new Set();
    userListeners.add(listener);
    this.listenersByUserId.set(userId, userListeners);

    return () => {
      const currentListeners = this.listenersByUserId.get(userId);
      if (!currentListeners) {
        return;
      }
      currentListeners.delete(listener);
      if (currentListeners.size === 0) {
        this.listenersByUserId.delete(userId);
      }
    };
  }

  subscribeAll(listener: SnapshotListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setMeeting(
    meeting: MeetingClient | null,
    generation: number,
    lifecycleHandlers: MeetingLifecycleHandlers = {}
  ) {
    if (generation < this.generation) {
      return;
    }
    if (this.meeting === meeting && this.generation === generation) {
      return;
    }

    const isNewGeneration = generation > this.generation;
    this.detachMeeting();
    this.clearRemotes();
    if (isNewGeneration) {
      this.participantRevisionByPeerId.clear();
    }
    this.meeting = meeting;
    this.generation = generation;
    this.isOperationCurrent = lifecycleHandlers.isCurrent ?? null;
    if (this.localCallSnapshot.generation < generation) {
      this.setLocalCallPhase("idle", generation);
    } else {
      this.updateLocalProjectionSequence();
      this.updateParticipantProjectionSequence(this.projectionSequence);
    }

    if (!meeting) {
      return;
    }

    const isCurrent = () => this.isCurrentMeeting(meeting, generation);
    const roomJoinedListener = () => {
      if (isCurrent()) {
        lifecycleHandlers.onRoomJoined?.();
        this.setLocalCallPhase("joined", generation);
      }
    };
    const roomLeftListener = ({ state }: { state: string }) => {
      if (!isCurrent()) {
        return;
      }
      if (!lifecycleHandlers.retainRemotesOnRoomLeft?.(state)) {
        this.clearRemotes();
      }
      this.setLocalCallPhase(
        lifecycleHandlers.getRoomLeftPhase?.(state) ?? "terminal",
        generation
      );
      lifecycleHandlers.onRoomLeft?.(state);
    };
    const socketConnectionListener = ({ state }: { state: string }) => {
      if (state === "reconnecting" && isCurrent()) {
        this.setLocalCallPhase("reconnecting", generation);
      }
    };
    const mediaConnectionListener = ({ state }: { state: string }) => {
      if (!isCurrent()) {
        return;
      }
      if (RECOVERING_MEDIA_CONNECTION_STATES.has(state)) {
        this.setLocalCallPhase("reconnecting", generation);
        return;
      }
      if (
        state === "connected" &&
        meeting.self.roomJoined &&
        TableCallParticipantDirectory.areMediaConnectionsHealthy(meeting)
      ) {
        this.setLocalCallPhase("joined", generation);
      }
    };
    const autoplayErrorListener = (error: Error) => {
      if (isCurrent()) {
        console.warn("[TableCall] RTK autoplay error", error);
      }
    };
    meeting.self.addListener("roomJoined", roomJoinedListener);
    meeting.self.addListener("roomLeft", roomLeftListener);
    meeting.self.addListener("autoplayError", autoplayErrorListener);
    meeting.meta.addListener(
      "socketConnectionUpdate",
      socketConnectionListener
    );
    meeting.meta.addListener("mediaConnectionUpdate", mediaConnectionListener);
    this.meetingListenerCleanups.add(() => {
      meeting.self.removeListener("roomJoined", roomJoinedListener);
      meeting.self.removeListener("roomLeft", roomLeftListener);
      meeting.self.removeListener("autoplayError", autoplayErrorListener);
      meeting.meta.removeListener(
        "socketConnectionUpdate",
        socketConnectionListener
      );
      meeting.meta.removeListener(
        "mediaConnectionUpdate",
        mediaConnectionListener
      );
    });

    const screenShareListener = ({
      id,
      screenShareEnabled,
      screenShareTracks,
    }: {
      id: string;
      screenShareEnabled: boolean;
      screenShareTracks: { audio?: MediaStreamTrack | null };
    }) => {
      if (!isCurrent()) {
        return;
      }
      this.setScreenShareAudioTrack(
        id,
        screenShareEnabled ? screenShareTracks.audio : null
      );
    };
    meeting.participants.joined.addListener(
      "screenShareUpdate",
      screenShareListener
    );
    this.meetingListenerCleanups.add(() => {
      meeting.participants.joined.removeListener(
        "screenShareUpdate",
        screenShareListener
      );
    });
    for (const eventName of PARTICIPANT_EVENTS) {
      const joinedListener = () => {
        if (isCurrent()) {
          this.reconcileJoined();
        }
      };
      const subscriptionsListener = () => {
        if (isCurrent()) {
          this.reconcileSubscriptions();
        }
      };
      meeting.participants.joined.addListener(eventName, joinedListener);
      meeting.participants.audioSubscribed.addListener(
        eventName,
        subscriptionsListener
      );
      this.meetingListenerCleanups.add(() => {
        meeting.participants.joined.removeListener(eventName, joinedListener);
        meeting.participants.audioSubscribed.removeListener(
          eventName,
          subscriptionsListener
        );
      });
    }
    if (isCurrent()) {
      this.reconcileJoined();
    }
    if (meeting.self.roomJoined && isCurrent()) {
      roomJoinedListener();
    }
  }

  dispose() {
    this.setMeeting(null, this.generation + 1);
    this.listenersByUserId.clear();
    this.listeners.clear();
    this.localCallListeners.clear();
  }

  private readonly reconcileJoined = () => {
    const { meeting } = this;
    if (!meeting) {
      return;
    }

    const joinedByPeerId = new Map<string, Participant>();
    for (const participant of meeting.participants.joined.toArray()) {
      joinedByPeerId.set(participant.id, participant);
    }

    for (const peerId of this.remotesByPeerId.keys()) {
      if (!joinedByPeerId.has(peerId)) {
        this.removeRemote(peerId);
      }
    }

    for (const participant of joinedByPeerId.values()) {
      this.upsertJoinedParticipant(participant);
    }
    this.reconcileSubscriptions();
  };

  private readonly reconcileSubscriptions = () => {
    const { meeting } = this;
    if (!meeting) {
      return;
    }

    const subscribedByPeerId = new Map<string, Participant>();
    for (const participant of meeting.participants.audioSubscribed.toArray()) {
      subscribedByPeerId.set(participant.id, participant);
    }

    for (const [peerId, remote] of this.remotesByPeerId) {
      const subscribed = subscribedByPeerId.get(peerId);
      const phase: RemoteCallPhase = subscribed ? "ready" : "joining";
      const consumerTrackId = subscribed?.audioTrack?.id;
      const consumerTrack = subscribed?.audioTrack ?? null;
      const previousConsumerTrack =
        this.consumerTracksByPeerId.get(peerId) ?? null;
      if (consumerTrack) {
        this.consumerTracksByPeerId.set(peerId, consumerTrack);
      } else {
        this.consumerTracksByPeerId.delete(peerId);
      }
      if (
        remote.phase === phase &&
        remote.consumerTrackId === consumerTrackId &&
        previousConsumerTrack === consumerTrack
      ) {
        continue;
      }
      this.setRemote({
        ...remote,
        consumerTrackId,
        participantRevision: remote.participantRevision + 1,
        phase,
      });
    }
  };

  private upsertJoinedParticipant(participant: Participant) {
    const participantId = resolveParticipantUserId(participant);
    if (!participantId) {
      return;
    }

    const existing = this.remotesByPeerId.get(participant.id);
    if (!existing) {
      this.joinOrderByPeerId.set(participant.id, this.nextJoinOrder++);
      this.setRemote({
        audioEnabled: participant.audioEnabled,
        participantId,
        participantRevision: this.incrementParticipantRevision(participant.id),
        peerId: participant.id,
        phase: "joining",
      });
      this.attachAudioUpdateListener(participant);
      this.setScreenShareAudioTrack(
        participant.id,
        participant.screenShareEnabled
          ? participant.screenShareTracks.audio
          : null
      );
      return;
    }

    if (
      existing.participantId !== participantId ||
      existing.audioEnabled !== participant.audioEnabled
    ) {
      this.setRemote({
        ...existing,
        audioEnabled: participant.audioEnabled,
        participantId,
        participantRevision: existing.participantRevision + 1,
      });
    }
    this.detachAudioUpdateListenersForPeer(participant.id, participant);
    this.attachAudioUpdateListener(participant);
    this.setScreenShareAudioTrack(
      participant.id,
      participant.screenShareEnabled
        ? participant.screenShareTracks.audio
        : null
    );
  }

  private attachAudioUpdateListener(participant: Participant) {
    if (this.audioUpdateListeners.has(participant)) {
      return;
    }
    const { generation, meeting } = this;
    if (!meeting) {
      return;
    }
    const listener = () => {
      if (
        !this.isCurrentMeeting(meeting, generation) ||
        meeting.participants.joined.get(participant.id) !== participant
      ) {
        return;
      }
      const remote = this.remotesByPeerId.get(participant.id);
      if (!remote) {
        return;
      }
      const subscribed = meeting.participants.audioSubscribed.get(
        participant.id
      );
      const consumerTrack = subscribed?.audioTrack ?? null;
      if (consumerTrack) {
        this.consumerTracksByPeerId.set(participant.id, consumerTrack);
      } else {
        this.consumerTracksByPeerId.delete(participant.id);
      }
      this.setRemote({
        ...remote,
        audioEnabled: participant.audioEnabled,
        consumerTrackId: subscribed?.audioTrack?.id,
        participantRevision: remote.participantRevision + 1,
      });
    };
    this.audioUpdateListeners.set(participant, listener);
    participant.addListener("audioUpdate", listener);
  }

  private setRemote(remote: RemoteCallState) {
    this.participantRevisionByPeerId.set(
      remote.peerId,
      Math.max(
        this.participantRevisionByPeerId.get(remote.peerId) ?? 0,
        remote.participantRevision
      )
    );
    this.remotesByPeerId.set(remote.peerId, remote);
    this.rebuildSnapshots();
  }

  private removeRemote(peerId: string) {
    this.incrementParticipantRevision(peerId);
    this.detachAudioUpdateListenersForPeer(peerId);
    this.remotesByPeerId.delete(peerId);
    this.consumerTracksByPeerId.delete(peerId);
    this.screenShareAudioTracksByPeerId.delete(peerId);
    this.joinOrderByPeerId.delete(peerId);
    this.rebuildSnapshots();
  }

  private setScreenShareAudioTrack(
    peerId: string,
    track: MediaStreamTrack | null | undefined
  ) {
    if (!this.remotesByPeerId.has(peerId)) {
      return;
    }
    const currentTrack = this.screenShareAudioTracksByPeerId.get(peerId);
    if (currentTrack === track) {
      return;
    }
    if (track) {
      this.screenShareAudioTracksByPeerId.set(peerId, track);
    } else {
      this.screenShareAudioTracksByPeerId.delete(peerId);
    }
    this.rebuildSnapshots();
  }

  private rebuildSnapshots() {
    const previousUserIds = new Set(this.snapshotsByUserId.keys());
    const nextSnapshots = new Map<string, ParticipantSnapshot>();
    const sequence = this.nextProjectionSequence();
    for (const remote of this.remotesByPeerId.values()) {
      const existing = nextSnapshots.get(remote.participantId)?.remote;
      if (!existing || this.isRemoteMoreRecent(remote, existing)) {
        nextSnapshots.set(remote.participantId, { remote, sequence });
      }
    }
    this.snapshotsByUserId = nextSnapshots;
    this.updateLocalProjectionSequence(sequence);
    for (const userId of nextSnapshots.keys()) {
      previousUserIds.add(userId);
    }
    for (const userId of previousUserIds) {
      this.notifyUser(userId);
    }
    for (const listener of this.listeners) {
      listener();
    }
  }

  private nextProjectionSequence() {
    this.projectionSequence += 1;
    return this.projectionSequence;
  }

  private incrementParticipantRevision(peerId: string) {
    const revision = (this.participantRevisionByPeerId.get(peerId) ?? 0) + 1;
    this.participantRevisionByPeerId.set(peerId, revision);
    return revision;
  }

  private updateLocalProjectionSequence(
    sequence = this.nextProjectionSequence()
  ) {
    this.localCallSnapshot = {
      ...this.localCallSnapshot,
      revision: sequence,
      sequence,
    };
    for (const listener of this.localCallListeners) {
      listener();
    }
  }

  private updateParticipantProjectionSequence(sequence: number) {
    if (this.snapshotsByUserId.size === 0) {
      return;
    }
    this.snapshotsByUserId = new Map(
      [...this.snapshotsByUserId].map(([userId, snapshot]) => [
        userId,
        { ...snapshot, sequence },
      ])
    );
    for (const userId of this.snapshotsByUserId.keys()) {
      this.notifyUser(userId);
    }
    for (const listener of this.listeners) {
      listener();
    }
  }

  private clearRemotes() {
    if (this.remotesByPeerId.size === 0) {
      return;
    }
    for (const peerId of this.remotesByPeerId.keys()) {
      this.incrementParticipantRevision(peerId);
    }
    for (const participant of this.audioUpdateListeners.keys()) {
      this.detachAudioUpdateListener(participant);
    }
    this.remotesByPeerId.clear();
    this.consumerTracksByPeerId.clear();
    this.screenShareAudioTracksByPeerId.clear();
    this.joinOrderByPeerId.clear();
    this.rebuildSnapshots();
  }

  private detachMeeting() {
    for (const cleanup of this.meetingListenerCleanups) {
      cleanup();
    }
    this.meetingListenerCleanups.clear();
    this.meeting = null;
    this.isOperationCurrent = null;
  }

  private detachAudioUpdateListener(participant: Participant) {
    const listener = this.audioUpdateListeners.get(participant);
    if (!listener) {
      return;
    }
    participant.removeListener("audioUpdate", listener);
    this.audioUpdateListeners.delete(participant);
  }

  private detachAudioUpdateListenersForPeer(
    peerId: string,
    except?: Participant
  ) {
    for (const participant of this.audioUpdateListeners.keys()) {
      if (participant.id === peerId && participant !== except) {
        this.detachAudioUpdateListener(participant);
      }
    }
  }

  private isRemoteMoreRecent(
    candidate: RemoteCallState,
    current: RemoteCallState
  ) {
    return (
      (this.joinOrderByPeerId.get(candidate.peerId) ?? 0) >
      (this.joinOrderByPeerId.get(current.peerId) ?? 0)
    );
  }

  private static areMediaConnectionsHealthy(meeting: MeetingClient) {
    return Object.values(meeting.meta.mediaState).every(
      (transport) => transport?.state === "connected"
    );
  }

  private isCurrentMeeting(meeting: MeetingClient, generation: number) {
    return (
      this.meeting === meeting &&
      this.generation === generation &&
      (this.isOperationCurrent?.() ?? true)
    );
  }

  private notifyUser(userId: string) {
    for (const listener of this.listenersByUserId.get(userId) ?? []) {
      listener();
    }
  }
}

export const getTableCallStatus = (phase: LocalCallPhase): TableCallStatus => {
  if (phase === "joined") {
    return "connected";
  }
  if (phase === "joining" || phase === "reconnecting") {
    return "joining";
  }
  return "disconnected";
};
